/**
 * TradeCorridorMap.jsx — GRANULLO_TRADE_CORRIDOR_LIVING_ORGANISM_V2
 *
 * Living global commerce truth organism.
 * Pulse packets traveling routes. Hub dominance. Inline risk markers.
 * Cinematic footprint. Lens geographic cognition.
 */
import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Ship, AlertTriangle, Shield } from 'lucide-react';

const SCARLET = '#CD2200';
const EMERALD = '#10B981';
const GOLD = '#F5A623';
const PURPLE = '#818CF8';

const HUBS = {
  dubai:      { x: 555, y: 145, label: 'Dubai' },
  mumbai:     { x: 615, y: 165, label: 'Mumbai' },
  singapore:  { x: 660, y: 195, label: 'Singapore' },
  // Rotterdam & Hamburg are geographically close — split their labels
  // so they never collide (Hamburg above, Rotterdam below its dot).
  rotterdam:  { x: 458, y: 102, label: 'Rotterdam', labelOffset: { x: -6, y: 22 }, labelBelow: true },
  hamburg:    { x: 478, y: 95,  label: 'Hamburg',   labelOffset: { x: 6,  y: 0  } },
  shanghai:   { x: 690, y: 135, label: 'Shanghai' },
  lagos:      { x: 440, y: 195, label: 'Lagos' },
  eastafrica: { x: 530, y: 210, label: 'E. Africa' },
  brazil:     { x: 255, y: 190, label: 'Brazil', labelOffset: { x: -20, y: 4 } },
};

const PT_COLORS = { boundary: EMERALD, trigger: GOLD, hybrid: SCARLET };

const CORRIDORS = [
  {
    id: 'dubai-mumbai', from: 'dubai', to: 'mumbai', commodity: 'Oil, Metals, Textiles',
    risks: [{ pos: 0.35, label: 'BL quantity mismatch' }, { pos: 0.65, label: 'HS code divergence' }],
    missions: ['hin_leong_collapse', 'qingdao_port_fraud'], future: ['autonomous_tranche_release'],
    proofType: 'hybrid', narrative: 'Today: duplicate collateral across lenders. Granullo: fail-closed settlement with receipt uniqueness.',
    currentFailure: ['Bill of Lading accepted without cross-reference to customs declaration', 'Warehouse receipts duplicated across 3 lenders without shared registry', 'Customs clearance released before IoT cargo verification'],
    granulloLogic: ['BL matched against customs declaration and IoT sensors', 'Warehouse receipt uniqueness enforced cross-bank', 'Customs release blocked until cargo verification completes', 'Payment resumes automatically after documentary truth clears'],
    downside: { exposure: '$35M–$120M', cascade: '2–5x lender', freeze: '72hr settlement', prevented: '$48M–$120M' },
    whyPlain: 'Payment to Mumbai supplier paused: Bill of Lading quantity does not match customs declaration. Auto-release resumes once corrected.',
    impact: { hours: '72h', cost: '$48M', issues: 'BL mismatch + duplicate collateral' },
  },
  {
    id: 'singapore-rotterdam', from: 'singapore', to: 'rotterdam', commodity: 'Crude Oil, LNG, Palm Oil',
    risks: [{ pos: 0.3, label: 'COO missing' }, { pos: 0.55, label: 'Sanctions unresolved' }, { pos: 0.8, label: 'Insurance certificate expired' }],
    missions: ['phantom_shipping', 'trade_sanctions_drift'], future: ['ai_shipping_fraud'],
    proofType: 'trigger', narrative: 'Today: BL accepted without vessel verification. Granullo: AIS-matched documentary release.',
    currentFailure: ['Certificate of Origin missing from shipping document set', 'Sanctions screened only at issuance, not during 45-day voyage', 'Insurance voided after mid-voyage sanctions change with no fallback'],
    granulloLogic: ['COO validated before payment instruction accepted', 'Continuous sanctions monitoring throughout voyage duration', 'Insurance auto-adjusts with alternative destination trigger', 'Settlement blocked until full documentary truth set clears'],
    downside: { exposure: '$200M–$800M', cascade: '3–7x counterparty', freeze: '45-day voyage', prevented: '$200M–$800M' },
    whyPlain: 'Settlement for Rotterdam crude cargo paused: Certificate of Origin missing + sanctions screen expired mid-voyage. Release resumes after documentation verified.',
    impact: { hours: '45d voyage', cost: '$200M', issues: 'COO + sanctions gap' },
  },
  {
    id: 'shanghai-hamburg', from: 'shanghai', to: 'hamburg', commodity: 'Electronics, Machinery',
    risks: [{ pos: 0.4, label: 'Container mismatch' }, { pos: 0.7, label: 'Inspection fraud' }],
    missions: ['lc_documentary_fraud'], future: ['trade_finance_ai'],
    proofType: 'boundary', narrative: 'Today: LC documents pass face-value only. Granullo: digital twin verification before release.',
    currentFailure: ['Container IDs not cross-verified against port loading records', 'Inspection certificates accepted from unverified sources', 'LC documents examined on face value only per UCP 600'],
    granulloLogic: ['Container ID verified against port camera + IoT', 'Inspection certificates attested by independent verifier', 'Release blocked until digital twin matches physical event'],
    downside: { exposure: '$50M–$300M', cascade: '2–4x documentary', freeze: '60-day LC', prevented: '$50M–$300M' },
  },
  {
    id: 'dubai-eastafrica', from: 'dubai', to: 'eastafrica', commodity: 'Consumer Goods, Equipment',
    risks: [{ pos: 0.4, label: 'Beneficiary drift' }, { pos: 0.7, label: 'Phantom delivery' }],
    missions: ['afghan_aid', 'ebola_aid'], future: ['corridor_automation_failure'],
    proofType: 'hybrid', narrative: 'Today: aid releases before impact confirmation. Granullo: milestone-gated disbursement.',
    currentFailure: ['Beneficiary lists manipulated by local intermediaries', 'Delivery receipts signed without verification', 'Aid capital released before impact confirmation'],
    granulloLogic: ['Biometric beneficiary verification at delivery', 'GPS-tracked delivery with weight confirmation', 'Release gated by verified impact milestone'],
    downside: { exposure: '$15M–$80M', cascade: '1–3x diversion', freeze: 'Monthly cycles', prevented: '$15M–$80M' },
  },
  {
    id: 'brazil-mumbai', from: 'brazil', to: 'mumbai', commodity: 'Commodities, Iron Ore, Sugar',
    risks: [{ pos: 0.3, label: 'Duplicate pledge' }, { pos: 0.6, label: 'FX corridor freeze' }],
    missions: ['hin_leong_collapse', 'fintech_collapse'], future: ['trade_sanctions_drift'],
    proofType: 'hybrid', narrative: 'Today: same cargo pledged across banks. Granullo: centralized collateral registry.',
    currentFailure: ['Same commodity cargo pledged at multiple banks simultaneously', 'FX settlement proceeds before sanctions re-screening', 'Invoice financing approved for prospective future sales'],
    granulloLogic: ['Centralized collateral registry blocks duplicate pledges', 'FX settlement re-screened at every stage', 'Invoice verified against actual delivery before financing'],
    downside: { exposure: '$48M–$150M', cascade: '3–6x multi-bank', freeze: '90-day settlement', prevented: '$48M–$150M' },
  },
  {
    id: 'lagos-dubai', from: 'lagos', to: 'dubai', commodity: 'Oil, Cocoa, Minerals',
    risks: [{ pos: 0.35, label: 'Sanctions gap' }, { pos: 0.65, label: 'Cargo diversion' }],
    missions: ['blood_diamond', 'qingdao_port_fraud'], future: ['autonomous_tranche_release'],
    proofType: 'trigger', narrative: 'Today: provenance breaks at cutting. Granullo: mine-to-market provenance lock.',
    currentFailure: ['Mineral provenance unverifiable after cutting/processing', 'Sanctions screening gaps between jurisdictions', 'Cargo diversion possible between origin and destination'],
    granulloLogic: ['Stone-level provenance tracked from mine to market', 'Cross-border sanctions screening at every transfer', 'GPS cargo tracking with automated diversion alerts'],
    downside: { exposure: '$20M–$90M', cascade: '2–4x jurisdictional', freeze: '30-day transit', prevented: '$20M–$90M' },
  },
];

function getQuadPath(from, to) {
  const midX = (from.x + to.x) / 2;
  const midY = Math.min(from.y, to.y) - 30 - Math.abs(from.x - to.x) * 0.06;
  return { path: `M${from.x},${from.y} Q${midX},${midY} ${to.x},${to.y}`, midX, midY };
}

function getPointOnQuad(from, to, midX, midY, t) {
  const x = (1 - t) * (1 - t) * from.x + 2 * (1 - t) * t * midX + t * t * to.x;
  const y = (1 - t) * (1 - t) * from.y + 2 * (1 - t) * t * midY + t * t * to.y;
  return { x, y };
}

export default function TradeCorridorMap() {
  const [active, setActive] = useState(null);
  const [locked, setLocked] = useState(false); // true = click-locked, false = hover-only preview
  const [pulseT, setPulseT] = useState(0);
  const [idleHighlight, setIdleHighlight] = useState(0);
  const [showShareModal, setShowShareModal] = useState(null);
  const [showTalkModal, setShowTalkModal] = useState(null);
  const [talkForm, setTalkForm] = useState({ name: '', email: '', company: '' });
  const [talkSent, setTalkSent] = useState(false);
  const dismissTimerRef = useRef(null);
  const containerRef = useRef(null);

  const API = process.env.REACT_APP_BACKEND_URL;

  // Sticky activation: hover = preview, click = lock
  const previewCorridor = (id) => {
    if (!locked) {
      clearTimeout(dismissTimerRef.current);
      setActive(id);
    }
  };

  const lockCorridor = (id) => {
    setActive(id);
    setLocked(true);
  };

  const unlockCorridor = () => {
    setLocked(false);
    setActive(null);
  };

  const handleMouseLeaveContainer = () => {
    if (!locked) {
      // 1000ms graceful delay before dismiss
      dismissTimerRef.current = setTimeout(() => {
        setActive(null);
      }, 1000);
    }
  };

  const handleMouseEnterContainer = () => {
    clearTimeout(dismissTimerRef.current);
  };

  // Click outside to unlock
  useEffect(() => {
    if (!locked) return;
    const handleClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        unlockCorridor();
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [locked]);

  // Track corridor events for GTM demand radar
  const trackEvent = (corridorId, eventType, corridor) => {
    const token = localStorage.getItem('granullo_token') || localStorage.getItem('token');
    fetch(`${API}/api/founder/marketing/corridor-event`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
      body: JSON.stringify({
        corridor_id: corridorId, event_type: eventType,
        persona: localStorage.getItem('granullo_demo_persona') || null,
        exposure_band: corridor?.downside?.exposure || null,
        mission_family: corridor?.missions?.[0] || null,
        commodity: corridor?.commodity || null,
      }),
    }).catch(() => {});
  };

  // Pulse animation + idle rotation
  useEffect(() => {
    let frame;
    let idleTimer = 0;
    const animate = () => {
      setPulseT(t => (t + 0.004) % 1);
      idleTimer++;
      // Rotate idle highlight every ~4 seconds (240 frames)
      if (idleTimer % 240 === 0) {
        setIdleHighlight(h => (h + 1) % CORRIDORS.length);
      }
      frame = requestAnimationFrame(animate);
    };
    frame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frame);
  }, []);

  // Conviction Pack canvas export
  const exportConvictionPack = (corridor) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const W = 1200, H = 800;
    canvas.width = W; canvas.height = H;

    // Background
    ctx.fillStyle = '#0B1120';
    ctx.fillRect(0, 0, W, H);

    // Gold accent line
    ctx.fillStyle = GOLD;
    ctx.fillRect(0, 0, W, 3);

    // Header
    ctx.textAlign = 'left';
    ctx.fillStyle = SCARLET;
    ctx.font = '600 11px "SF Mono", monospace';
    ctx.fillText('GRANULLO TRADE CORRIDOR CONVICTION PACK', 40, 40);

    ctx.fillStyle = '#F0F2F5';
    ctx.font = '600 28px Georgia, serif';
    ctx.fillText(`${HUBS[corridor.from]?.label} → ${HUBS[corridor.to]?.label}`, 40, 78);

    const color = PT_COLORS[corridor.proofType] || SCARLET;
    ctx.fillStyle = color;
    ctx.font = '600 11px "SF Mono", monospace';
    ctx.fillText(`◈ ${corridor.proofType.toUpperCase()} | ${corridor.commodity}`, 40, 100);

    // Divider
    ctx.strokeStyle = 'rgba(255,255,255,0.08)';
    ctx.beginPath(); ctx.moveTo(40, 115); ctx.lineTo(W - 40, 115); ctx.stroke();

    let y = 140;

    // LEFT column: Current System Failure
    ctx.fillStyle = SCARLET;
    ctx.font = '600 10px "SF Mono", monospace';
    ctx.fillText('CURRENT SYSTEM FAILURE', 40, y);
    y += 18;
    ctx.fillStyle = 'rgba(255,255,255,0.6)';
    ctx.font = '13px Georgia, serif';
    (corridor.currentFailure || []).forEach(f => {
      ctx.fillText(`• ${f}`, 50, y);
      y += 20;
    });

    // RIGHT column: Granullo Release Logic
    let ry = 140;
    ctx.fillStyle = EMERALD;
    ctx.font = '600 10px "SF Mono", monospace';
    ctx.fillText('GRANULLO RELEASE LOGIC', W / 2 + 20, ry);
    ry += 18;
    ctx.fillStyle = 'rgba(255,255,255,0.6)';
    ctx.font = '13px Georgia, serif';
    (corridor.granulloLogic || []).forEach(g => {
      ctx.fillText(`✓ ${g}`, W / 2 + 30, ry);
      ry += 20;
    });

    y = Math.max(y, ry) + 20;

    // Divider
    ctx.strokeStyle = 'rgba(255,255,255,0.08)';
    ctx.beginPath(); ctx.moveTo(40, y); ctx.lineTo(W - 40, y); ctx.stroke();
    y += 20;

    // Economic Downside
    if (corridor.downside) {
      ctx.fillStyle = GOLD;
      ctx.font = '600 10px "SF Mono", monospace';
      ctx.fillText('ECONOMIC DOWNSIDE PREVENTED', 40, y);
      y += 22;

      ctx.font = '600 22px "SF Mono", monospace';
      ctx.fillStyle = EMERALD;
      ctx.fillText(corridor.downside.prevented, 40, y);

      ctx.fillStyle = 'rgba(255,255,255,0.4)';
      ctx.font = '11px "SF Mono", monospace';
      ctx.fillText(`Exposure: ${corridor.downside.exposure}`, 300, y);
      ctx.fillText(`Cascade: ${corridor.downside.cascade}`, 500, y);
      ctx.fillText(`Freeze: ${corridor.downside.freeze}`, 680, y);
      y += 30;
    }

    // Divider
    ctx.strokeStyle = 'rgba(255,255,255,0.08)';
    ctx.beginPath(); ctx.moveTo(40, y); ctx.lineTo(W - 40, y); ctx.stroke();
    y += 20;

    // Mission Family + Future
    ctx.fillStyle = 'rgba(255,255,255,0.4)';
    ctx.font = '10px "SF Mono", monospace';
    ctx.fillText('MISSION FAMILY', 40, y);
    ctx.fillText('FUTURE BRANCH', W / 2 + 20, y);
    y += 16;
    ctx.fillStyle = '#F0F2F5';
    ctx.font = '12px Georgia, serif';
    (corridor.missions || []).forEach((m, i) => {
      ctx.fillText(m.replace(/_/g, ' '), 50, y + i * 16);
    });
    ctx.fillStyle = PURPLE;
    (corridor.future || []).forEach((m, i) => {
      ctx.fillText(m.replace(/_/g, ' '), W / 2 + 30, y + i * 16);
    });

    // Footer
    ctx.fillStyle = 'rgba(255,255,255,0.3)';
    ctx.font = '9px "SF Mono", monospace';
    ctx.textAlign = 'center';
    ctx.fillText('Generated by Granullo — The world\'s largest programmable trust failure simulation universe', W / 2, H - 30);
    ctx.fillText(`granullo.com?demo=trade&corridor=${corridor.id}`, W / 2, H - 16);

    // Download
    const a = document.createElement('a');
    a.href = canvas.toDataURL('image/png');
    a.download = `granullo-trade-finance-${corridor.from}-${HUBS[corridor.to]?.label?.toLowerCase() || corridor.to}-${corridor.proofType}.png`;
    a.click();
  };
  useEffect(() => {
    if (active) {
      const c = CORRIDORS.find(x => x.id === active);
      if (c) {
        window.dispatchEvent(new CustomEvent('granullo-corridor-focus', {
          detail: { corridor: c.id, narrative: c.narrative, proofType: c.proofType }
        }));
      }
    } else {
      window.dispatchEvent(new CustomEvent('granullo-corridor-blur'));
    }
    return () => {
      // Always clear corridor narrative when this component unmounts or active changes
      window.dispatchEvent(new CustomEvent('granullo-corridor-blur'));
    };
  }, [active]);

  // Hub-first discovery: map hub IDs to their primary corridor
  const HUB_PRIMARY_CORRIDOR = {
    dubai: 'dubai-mumbai', mumbai: 'dubai-mumbai', singapore: 'singapore-rotterdam',
    rotterdam: 'singapore-rotterdam', hamburg: 'shanghai-hamburg', shanghai: 'shanghai-hamburg',
    lagos: 'lagos-dubai', eastafrica: 'dubai-eastafrica', brazil: 'brazil-mumbai',
  };

  return (
    <section style={{ padding: '80px 0 60px', position: 'relative' }} data-testid="trade-corridor-map">
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 24px' }}>

        {/* ═══ HERO ═══ */}
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.1fr) minmax(0, 0.9fr)', gap: 32, alignItems: 'end', marginBottom: 22 }}>
          <div>
            <p style={{ fontSize: 10, fontFamily: "'SF Mono', monospace", textTransform: 'uppercase', letterSpacing: '0.22em', color: SCARLET, marginBottom: 10 }}>
              <Ship size={11} style={{ display: 'inline', verticalAlign: -2, marginRight: 6 }} />
              Trade Corridors · Live Atlas
            </p>
            <h2 style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontSize: 'clamp(32px, 4vw, 48px)', fontWeight: 600, color: 'var(--shell-text)', lineHeight: 1.03, letterSpacing: '-0.02em', marginBottom: 12 }}>
              Cargo moves at the speed of ships.<br/>
              <span style={{ color: SCARLET }}>Capital moves at the speed of proof.</span>
            </h2>
            <p style={{ fontFamily: "'Kalam', cursive", fontSize: 20, color: SCARLET, opacity: 0.75, transform: 'rotate(-1deg)', display: 'inline-block' }}>
              until documentary truth clears, nothing settles
            </p>
          </div>

          {/* Headline impact numbers */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
            {[
              { big: '$873B', label: 'moves through these corridors / day', accent: GOLD },
              { big: '2–5x', label: 'cascade when proof arrives late', accent: SCARLET },
              { big: '0', label: 'drift events under release-by-truth', accent: EMERALD },
            ].map((s, i) => (
              <div key={i} style={{
                padding: '14px 14px 12px', borderRadius: 10,
                background: 'var(--shell-surface)', border: '1px solid var(--shell-border)',
                position: 'relative', overflow: 'hidden',
              }}>
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: s.accent, opacity: 0.9 }} />
                <p style={{ fontFamily: "'SF Mono', monospace", fontSize: 22, fontWeight: 700, color: s.accent, lineHeight: 1, marginBottom: 6, letterSpacing: '-0.01em' }}>
                  {s.big}
                </p>
                <p style={{ fontSize: 10, color: 'var(--shell-text-muted)', lineHeight: 1.4 }}>{s.label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* ═══ 4-STEP STORY STRIP — connected by flowing arrows ═══ */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: 0,
            marginBottom: 24,
            position: 'relative',
            background: 'var(--shell-surface)',
            border: '1px solid var(--shell-border)',
            borderRadius: 12,
            overflow: 'hidden',
          }}
          data-testid="trade-story-strip"
        >
          {[
            { step: '1', label: 'Cargo departs', desc: 'Shipment leaves origin port', color: GOLD,   icon: '⛴' },
            { step: '2', label: 'Truth in transit', desc: 'BL · COO · sanctions · customs',    color: SCARLET, icon: '⟐' },
            { step: '3', label: 'Capital at rest',  desc: 'Nothing settles until proof arrives', color: SCARLET, icon: '⏸' },
            { step: '4', label: 'Release by truth', desc: 'Payment moves when proof lands',     color: EMERALD, icon: '✓' },
          ].map((s, i, arr) => (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', gap: 12,
              padding: '14px 18px',
              borderRight: i < arr.length - 1 ? '1px solid var(--shell-border)' : 'none',
              position: 'relative',
              background: i === arr.length - 1 ? `linear-gradient(90deg, transparent, ${EMERALD}08)` : 'transparent',
            }}>
              {/* Big step number */}
              <div style={{
                flexShrink: 0,
                width: 36, height: 36, borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: `${s.color}12`,
                border: `1.5px solid ${s.color}55`,
                color: s.color, fontFamily: "'SF Mono', monospace", fontSize: 15, fontWeight: 700,
                boxShadow: `0 0 16px ${s.color}25`,
              }}>
                {s.step}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--shell-text)', marginBottom: 2, letterSpacing: '-0.01em' }}>
                  {s.label}
                </p>
                <p style={{ fontSize: 10.5, color: 'var(--shell-text-muted)', lineHeight: 1.4 }}>{s.desc}</p>
              </div>
              {/* Flowing arrow between steps */}
              {i < arr.length - 1 && (
                <div style={{
                  position: 'absolute', right: -8, top: '50%', transform: 'translateY(-50%)',
                  width: 16, height: 16, borderRadius: '50%',
                  background: 'var(--shell-bg, #fff)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: 'var(--shell-text-muted)', fontSize: 11, fontWeight: 600,
                  zIndex: 2, border: '1px solid var(--shell-border)',
                }}>
                  →
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Interaction CTA */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
          <p style={{ fontSize: 11, color: 'var(--shell-text)', fontFamily: "'SF Mono', monospace", textTransform: 'uppercase', letterSpacing: '0.18em' }}>
            <span style={{ color: SCARLET, fontWeight: 700, marginRight: 8 }}>TAP A ROUTE →</span>
            <span style={{ color: 'var(--shell-text-muted)' }}>see where capital moves without proof today</span>
          </p>
          <p style={{ fontSize: 10, color: 'var(--shell-text-muted)', fontFamily: "'SF Mono', monospace" }}>
            {CORRIDORS.length} live corridors · {Object.keys(HUBS).length} hubs
          </p>
        </div>

        {/* Map — wide cinematic footprint */}
        <div ref={containerRef}
          onMouseEnter={handleMouseEnterContainer}
          onMouseLeave={handleMouseLeaveContainer}
          style={{
            position: 'relative',
            background: 'linear-gradient(180deg, var(--shell-surface) 0%, var(--shell-bg) 100%)',
            border: `1px solid ${locked ? '#CD220040' : 'var(--shell-border)'}`,
            borderRadius: 12,
            overflow: 'hidden',
            transition: 'border-color 0.3s, box-shadow 0.3s',
            boxShadow: locked
              ? `0 0 0 1px ${SCARLET}20, 0 20px 60px rgba(0,0,0,0.08)`
              : '0 8px 28px rgba(0,0,0,0.04)',
          }}>
          <svg viewBox="0 0 800 260" style={{ width: '100%', height: 'auto', display: 'block' }} data-testid="corridor-svg">
            {/* Subtle continent shapes — background, NOT hero */}
            <ellipse cx="460" cy="108" rx="50" ry="22" fill="var(--shell-border)" opacity="0.15" />
            <ellipse cx="520" cy="165" rx="65" ry="42" fill="var(--shell-border)" opacity="0.15" />
            <ellipse cx="660" cy="155" rx="60" ry="35" fill="var(--shell-border)" opacity="0.15" />
            <ellipse cx="255" cy="185" rx="42" ry="32" fill="var(--shell-border)" opacity="0.15" /> {/* S.America — centered on Brazil hub */}
            <ellipse cx="240" cy="120" rx="40" ry="22" fill="var(--shell-border)" opacity="0.15" />

            {/* Corridors */}
            {CORRIDORS.map((c, ci) => {
              const from = HUBS[c.from];
              const to = HUBS[c.to];
              if (!from || !to) return null;
              const { path, midX, midY } = getQuadPath(from, to);
              const isActive = active === c.id;
              const isIdleHighlight = !active && ci === idleHighlight;
              const isAlive = isActive || isIdleHighlight;
              const color = PT_COLORS[c.proofType] || SCARLET;

              // Pulse packet position
              const pulsePoint = getPointOnQuad(from, to, midX, midY, pulseT);

              return (
                <g key={c.id}
                  onMouseEnter={() => previewCorridor(c.id)}
                  
                  style={{ cursor: 'pointer' }}>
                  {/* WIDE INVISIBLE HIT ZONE */}
                  <path d={path} fill="none" stroke="transparent" strokeWidth="16"
                    onMouseEnter={() => previewCorridor(c.id)} onClick={() => lockCorridor(c.id)} />

                  {/* Route line — alive when active OR idle-highlighted */}
                  <path d={path} fill="none"
                    stroke={isAlive ? color : 'var(--shell-text-muted)'}
                    strokeWidth={isActive ? 2.5 : isIdleHighlight ? 1.5 : 1}
                    strokeDasharray={isActive ? 'none' : isIdleHighlight ? '6,4' : '3,5'}
                    opacity={isActive ? 0.9 : isIdleHighlight ? 0.45 : 0.25}
                    style={{ transition: 'stroke 0.3s, stroke-width 0.3s, opacity 0.5s' }} />

                  {/* Active glow trail */}
                  {isAlive && (
                    <path d={path} fill="none" stroke={color} strokeWidth={isActive ? 8 : 5} opacity={isActive ? 0.06 : 0.03}
                      style={{ filter: 'blur(6px)', transition: 'opacity 0.3s' }} />
                  )}

                  {/* Pulse packet — truth traveling (active + idle) */}
                  {isAlive && (
                    <>
                      <circle cx={pulsePoint.x} cy={pulsePoint.y} r={isActive ? 3.5 : 2} fill={color} opacity={isActive ? 0.9 : 0.5} />
                      {isActive && <circle cx={pulsePoint.x} cy={pulsePoint.y} r="7" fill={color} opacity="0.15" />}
                    </>
                  )}

                  {/* Inline risk markers — large vertical stagger + text halo to prevent any collision */}
                  {isActive && c.risks.map((r, ri) => {
                    const rp = getPointOnQuad(from, to, midX, midY, r.pos);
                    // Alternate far above / far below the route
                    const staggerY = ri % 2 === 0 ? -22 : 22;
                    const flipSide = ri % 2 === 0 ? 1 : -1;
                    const labelX = rp.x + 8 * flipSide;
                    const labelY = rp.y + staggerY + 3;
                    return (
                      <g key={ri} style={{ pointerEvents: 'none' }}>
                        {/* Leader line from route to label */}
                        <line
                          x1={rp.x} y1={rp.y}
                          x2={labelX - 2 * flipSide} y2={labelY - 2}
                          stroke={SCARLET} strokeWidth="0.5" opacity="0.45"
                        />
                        {/* Small risk dot on the route */}
                        <circle cx={rp.x} cy={rp.y} r="2.5" fill={SCARLET} opacity="0.85" />
                        {/* Label with white stroke halo so it never mixes with hub labels */}
                        <text
                          x={labelX} y={labelY}
                          textAnchor={flipSide > 0 ? 'start' : 'end'}
                          style={{
                            fontSize: 6.5, fontFamily: "'SF Mono', monospace",
                            fill: SCARLET, opacity: 0.95, fontWeight: 600,
                            paintOrder: 'stroke',
                            stroke: 'var(--shell-bg, #fff)',
                            strokeWidth: 3,
                            strokeLinejoin: 'round',
                          }}
                        >
                          {r.label}
                        </text>
                      </g>
                    );
                  })}
                </g>
              );
            })}

            {/* Hubs — dominant anchor nodes with corridor activation + breathing */}
            {Object.entries(HUBS).map(([id, hub]) => {
              const isEndpoint = CORRIDORS.some(c => (active === c.id) && (c.from === id || c.to === id));
              const isIdleEndpoint = !active && CORRIDORS.some((c, ci) => ci === idleHighlight && (c.from === id || c.to === id));
              const isAliveHub = isEndpoint || isIdleEndpoint;
              return (
                <g key={id}
                  onMouseEnter={() => { const pc = HUB_PRIMARY_CORRIDOR[id]; if (pc) previewCorridor(pc); }} onClick={() => { const pc = HUB_PRIMARY_CORRIDOR[id]; if (pc) lockCorridor(pc); }}
                  
                  style={{ cursor: 'pointer' }}>
                  {/* Wide hub hit zone */}
                  <circle cx={hub.x} cy={hub.y} r="18" fill="transparent" />
                  {/* Breathing pulse ring */}
                  {isAliveHub && (
                    <circle cx={hub.x} cy={hub.y} r="10" fill="none"
                      stroke={isEndpoint ? SCARLET : 'var(--shell-text-muted)'} strokeWidth="0.8" opacity="0.3">
                      <animate attributeName="r" values="6;12;6" dur="2.5s" repeatCount="indefinite" />
                      <animate attributeName="opacity" values="0.3;0;0.3" dur="2.5s" repeatCount="indefinite" />
                    </circle>
                  )}
                  {/* Hub ambient breathing for ALL hubs (subtle scale pulse) */}
                  <circle cx={hub.x} cy={hub.y}
                    r={isEndpoint ? 4.5 : isIdleEndpoint ? 3.5 : 3}
                    fill={isAliveHub ? (isEndpoint ? SCARLET : 'var(--shell-text-secondary)') : 'var(--shell-text-muted)'}
                    opacity={isAliveHub ? 1 : 0.65}
                    style={{ transition: 'all 0.4s' }} />
                  <text
                    x={hub.x + (hub.labelOffset?.x || 0)}
                    y={hub.y + (hub.labelBelow ? 10 : -8) + (hub.labelOffset?.y || 0)}
                    textAnchor="middle"
                    style={{
                      fontSize: 7.5, fontFamily: "'SF Mono', monospace", fontWeight: 600,
                      fill: isEndpoint ? 'var(--shell-text)' : 'var(--shell-text-secondary)',
                      opacity: isEndpoint ? 1 : 0.6,
                      transition: 'all 0.3s',
                      paintOrder: 'stroke',
                      stroke: 'var(--shell-bg, #fff)',
                      strokeWidth: 3,
                      strokeLinejoin: 'round',
                    }}>
                    {hub.label}
                  </text>
                </g>
              );
            })}
          </svg>

          {/* CORRIDOR TERMINAL STRIP — Below map, never overlapping geography */}
          <AnimatePresence>
            {active && (() => {
              const c = CORRIDORS.find(x => x.id === active);
              if (!c) return null;
              const color = PT_COLORS[c.proofType] || SCARLET;
              const ptSigil = c.proofType === 'boundary' ? '◆' : c.proofType === 'trigger' ? '▸' : '◈';
              return (
                <motion.div key={active}
                  initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.2 }}
                  style={{
                    background: 'var(--shell-bg)',
                    borderTop: `1px solid var(--shell-border)`,
                    padding: '10px 20px',
                    overflow: 'hidden',
                  }}
                  data-testid={`corridor-detail-${active}`}>
                  {/* Scarlet top glow line */}
                  <div style={{ position: 'absolute', top: -1, left: 0, right: 0, height: 2, background: `linear-gradient(90deg, transparent, ${color}, transparent)`, opacity: 0.5 }} />
                  
                  <div style={{ maxWidth: 1100, margin: '0 auto' }}>
                    {/* Route header */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 18, fontWeight: 600, color: 'var(--shell-text)', fontFamily: "'Source Serif 4', Georgia, serif", letterSpacing: '-0.01em' }}>
                        {HUBS[c.from]?.label} → {HUBS[c.to]?.label}
                      </span>
                      <span style={{ fontSize: 9, fontFamily: "'SF Mono', monospace", padding: '3px 8px', borderRadius: 3, color, background: `${color}12`, border: `1px solid ${color}40`, fontWeight: 700, letterSpacing: '0.1em' }}>
                        {ptSigil} {c.proofType.toUpperCase()}
                      </span>
                      <span style={{ fontSize: 11, color: 'var(--shell-text-muted)' }}>{c.commodity}</span>
                    </div>

                    {/* Before / After split */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                      {/* LEFT: Current System Failure */}
                      <div style={{
                        padding: '12px 14px',
                        background: `${SCARLET}06`,
                        border: `1px solid ${SCARLET}20`,
                        borderRadius: 8,
                      }}>
                        <p style={{ fontSize: 9, fontFamily: "'SF Mono', monospace", color: SCARLET, textTransform: 'uppercase', letterSpacing: '0.14em', fontWeight: 700, marginBottom: 8 }}>
                          <AlertTriangle size={10} style={{ display: 'inline', verticalAlign: -1, marginRight: 4 }} />
                          Today — capital moves blind
                        </p>
                        {(c.currentFailure || []).map((f, i) => (
                          <p key={i} style={{ fontSize: 11.5, color: 'var(--shell-text)', lineHeight: 1.5, marginBottom: 5, paddingLeft: 10, borderLeft: `2px solid ${SCARLET}35`, opacity: 0.85 }}>
                            {f}
                          </p>
                        ))}
                      </div>
                      {/* RIGHT: Granullo Release Logic */}
                      <div style={{
                        padding: '12px 14px',
                        background: `${EMERALD}06`,
                        border: `1px solid ${EMERALD}20`,
                        borderRadius: 8,
                      }}>
                        <p style={{ fontSize: 9, fontFamily: "'SF Mono', monospace", color: EMERALD, textTransform: 'uppercase', letterSpacing: '0.14em', fontWeight: 700, marginBottom: 8 }}>
                          <Shield size={10} style={{ display: 'inline', verticalAlign: -1, marginRight: 4 }} />
                          With Granullo — release by truth
                        </p>
                        {(c.granulloLogic || []).map((g, i) => (
                          <p key={i} style={{ fontSize: 11.5, color: 'var(--shell-text)', lineHeight: 1.5, marginBottom: 5, paddingLeft: 10, borderLeft: `2px solid ${EMERALD}35`, opacity: 0.85 }}>
                            <span style={{ color: EMERALD, fontWeight: 700, marginRight: 4 }}>✓</span>{g}
                          </p>
                        ))}
                      </div>
                    </div>

                    {/* PLAIN-LANGUAGE WHY + IMPACT STRIP */}
                    {c.whyPlain && (
                      <div style={{ background: `${SCARLET}04`, border: `1px solid ${SCARLET}12`, borderRadius: 8, padding: '8px 12px', marginTop: 8 }}>
                        <p style={{ fontSize: 11, color: 'var(--shell-text)', lineHeight: 1.5 }} data-testid="corridor-why-plain">{c.whyPlain}</p>
                        {c.impact && (
                          <div style={{ display: 'flex', gap: 12, marginTop: 6, fontSize: 8, fontFamily: "'SF Mono', monospace" }}>
                            <span style={{ color: SCARLET, fontWeight: 600 }}>Never occurred: {c.impact.hours} delay</span>
                            <span style={{ color: GOLD }}>{c.impact.cost} exposure</span>
                            <span style={{ color: 'var(--shell-text-muted)' }}>{c.impact.issues}</span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* ECONOMIC DOWNSIDE + ACTIVATION BRIDGE */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 16, marginTop: 8, paddingTop: 6, borderTop: '1px solid var(--shell-border)' }}>
                      {/* Left: Downside quantification + narrative */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                        <p style={{ fontFamily: "'Caveat', cursive", fontSize: 13, color: SCARLET }}>{c.narrative}</p>
                        {c.downside && (
                          <div style={{ display: 'flex', gap: 10, fontSize: 8, fontFamily: "'SF Mono', monospace" }}>
                            <span style={{ color: SCARLET }}>{c.downside.exposure} exposure</span>
                            <span style={{ color: 'var(--shell-text-muted)' }}>{c.downside.cascade} cascade</span>
                            <span style={{ color: EMERALD, fontWeight: 600 }}>Never occurred: {c.downside.prevented}</span>
                          </div>
                        )}
                      </div>
                      {/* Right: Escalating commitment CTAs */}
                      <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
                        {c.missions[0] && (
                          <a href={`/play/mission/${c.missions[0]}`} onClick={() => trackEvent(c.id, 'simulate', c)}
                            style={{ fontSize: 7, fontFamily: "'SF Mono', monospace", padding: '3px 7px', borderRadius: 3, background: `${SCARLET}08`, border: `1px solid ${SCARLET}18`, color: SCARLET, textDecoration: 'none', cursor: 'pointer', whiteSpace: 'nowrap' }}
                            data-testid="corridor-simulate-btn">
                            Simulate
                          </a>
                        )}
                        <button onClick={() => { trackEvent(c.id, 'export', c); exportConvictionPack(c); }}
                          style={{ fontSize: 7, fontFamily: "'SF Mono', monospace", padding: '3px 7px', borderRadius: 3, background: `${GOLD}08`, border: `1px solid ${GOLD}18`, color: GOLD, cursor: 'pointer', whiteSpace: 'nowrap' }}
                          data-testid="corridor-export-btn">
                          Export
                        </button>
                        <button onClick={() => { trackEvent(c.id, 'sandbox', c); window.open(`/sandbox?corridor=${c.id}`, '_self'); }}
                          style={{ fontSize: 7, fontFamily: "'SF Mono', monospace", padding: '3px 7px', borderRadius: 3, background: `${EMERALD}08`, border: `1px solid ${EMERALD}18`, color: EMERALD, cursor: 'pointer', whiteSpace: 'nowrap' }}
                          data-testid="corridor-sandbox-btn">
                          Sandbox
                        </button>
                        <span style={{ width: 1, height: 12, background: 'var(--shell-border)', margin: '0 2px' }} />
                        <button onClick={() => { trackEvent(c.id, 'talk', c); setShowTalkModal(c); }}
                          style={{ fontSize: 7, fontFamily: "'SF Mono', monospace", padding: '3px 7px', borderRadius: 3, background: `${PURPLE}10`, border: `1px solid ${PURPLE}20`, color: PURPLE, cursor: 'pointer', whiteSpace: 'nowrap', fontWeight: 600 }}
                          data-testid="corridor-talk-btn">
                          Talk to Granullo
                        </button>
                        <button onClick={() => { trackEvent(c.id, 'share', c); setShowShareModal(c); }}
                          style={{ fontSize: 7, fontFamily: "'SF Mono', monospace", padding: '3px 7px', borderRadius: 3, background: 'var(--shell-surface)', border: '1px solid var(--shell-border)', color: 'var(--shell-text-secondary)', cursor: 'pointer', whiteSpace: 'nowrap' }}
                          data-testid="corridor-share-btn">
                          Send Internally
                        </button>
                        <button onClick={() => { trackEvent(c.id, 'pilot_request', c); setShowTalkModal({...c, isPilot: true}); }}
                          style={{ fontSize: 7, fontFamily: "'SF Mono', monospace", padding: '3px 7px', borderRadius: 3, background: SCARLET, border: `1px solid ${SCARLET}`, color: '#fff', cursor: 'pointer', whiteSpace: 'nowrap', fontWeight: 600 }}
                          data-testid="corridor-pilot-btn">
                          Start Pilot
                        </button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })()}
          </AnimatePresence>
        </div>
      </div>

      {/* ═══ SHARE INTERNALLY MODAL ═══ */}
      <AnimatePresence>
        {showShareModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(6px)', padding: 24 }}
            onClick={() => setShowShareModal(null)}>
            <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} onClick={e => e.stopPropagation()}
              style={{ background: 'var(--shell-bg)', border: '1px solid var(--shell-border)', borderRadius: 12, padding: 24, maxWidth: 460, width: '100%' }}
              data-testid="share-modal">
              <p style={{ fontSize: 10, fontFamily: "'SF Mono', monospace", color: SCARLET, textTransform: 'uppercase', marginBottom: 10 }}>Send Corridor Conviction Internally</p>
              <p style={{ fontSize: 14, fontWeight: 600, color: 'var(--shell-text)', marginBottom: 4 }}>{HUBS[showShareModal.from]?.label} → {HUBS[showShareModal.to]?.label}</p>
              <p style={{ fontSize: 11, color: SCARLET, marginBottom: 12 }}>Preventable downside: {showShareModal.downside?.prevented}</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <button onClick={() => { navigator.clipboard.writeText(`${HUBS[showShareModal.from]?.label} → ${HUBS[showShareModal.to]?.label}: ${showShareModal.downside?.prevented} preventable exposure.\n\nCurrent failure: ${showShareModal.currentFailure?.[0]}\nGranullo: ${showShareModal.granulloLogic?.[0]}\n\nExplore: ${window.location.origin}?demo=trade&corridor=${showShareModal.id}`); }}
                  style={{ padding: '8px 12px', background: 'var(--shell-surface)', border: '1px solid var(--shell-border)', borderRadius: 6, color: 'var(--shell-text)', cursor: 'pointer', fontSize: 11, textAlign: 'left' }}>
                  Copy executive summary
                </button>
                <button onClick={() => { navigator.clipboard.writeText(`${window.location.origin}?demo=trade&corridor=${showShareModal.id}`); }}
                  style={{ padding: '8px 12px', background: 'var(--shell-surface)', border: '1px solid var(--shell-border)', borderRadius: 6, color: 'var(--shell-text)', cursor: 'pointer', fontSize: 11, textAlign: 'left' }}>
                  Copy demo link
                </button>
                <button onClick={() => { exportConvictionPack(showShareModal); setShowShareModal(null); }}
                  style={{ padding: '8px 12px', background: `${GOLD}10`, border: `1px solid ${GOLD}25`, borderRadius: 6, color: GOLD, cursor: 'pointer', fontSize: 11, textAlign: 'left' }}>
                  Download conviction pack PNG
                </button>
              </div>
              <button onClick={() => setShowShareModal(null)} style={{ marginTop: 10, width: '100%', padding: '6px', background: 'transparent', border: '1px solid var(--shell-border)', borderRadius: 6, color: 'var(--shell-text-muted)', cursor: 'pointer', fontSize: 10 }}>Close</button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ═══ TALK TO GRANULLO / START PILOT MODAL ═══ */}
      <AnimatePresence>
        {showTalkModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(6px)', padding: 24 }}
            onClick={() => { setShowTalkModal(null); setTalkSent(false); }}>
            <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} onClick={e => e.stopPropagation()}
              style={{ background: 'var(--shell-bg)', border: `1px solid ${showTalkModal.isPilot ? SCARLET : PURPLE}30`, borderRadius: 12, padding: 24, maxWidth: 420, width: '100%' }}
              data-testid="talk-modal">
              <p style={{ fontSize: 10, fontFamily: "'SF Mono', monospace", color: showTalkModal.isPilot ? SCARLET : PURPLE, textTransform: 'uppercase', marginBottom: 6 }}>
                {showTalkModal.isPilot ? 'Start a Pilot' : 'Talk to Granullo'}
              </p>
              <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--shell-text)', marginBottom: 3 }}>{HUBS[showTalkModal.from]?.label} → {HUBS[showTalkModal.to]?.label}</p>
              <p style={{ fontSize: 10, color: 'var(--shell-text-muted)', marginBottom: 12 }}>Preventable: {showTalkModal.downside?.prevented} | {showTalkModal.commodity}</p>
              {talkSent ? (
                <div style={{ textAlign: 'center', padding: 20 }}>
                  <p style={{ fontSize: 14, color: EMERALD, fontWeight: 600 }}>Received.</p>
                  <p style={{ fontSize: 11, color: 'var(--shell-text-muted)', marginTop: 4 }}>We'll reach out within 24 hours about the {showTalkModal.isPilot ? 'pilot' : 'corridor'}.</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <input placeholder="Your name" value={talkForm.name} onChange={e => setTalkForm(f => ({...f, name: e.target.value}))}
                    style={{ padding: '8px 10px', background: 'var(--shell-surface)', border: '1px solid var(--shell-border)', borderRadius: 6, color: 'var(--shell-text)', fontSize: 11 }} />
                  <input placeholder="Work email" value={talkForm.email} onChange={e => setTalkForm(f => ({...f, email: e.target.value}))}
                    style={{ padding: '8px 10px', background: 'var(--shell-surface)', border: '1px solid var(--shell-border)', borderRadius: 6, color: 'var(--shell-text)', fontSize: 11 }} />
                  <input placeholder="Company" value={talkForm.company} onChange={e => setTalkForm(f => ({...f, company: e.target.value}))}
                    style={{ padding: '8px 10px', background: 'var(--shell-surface)', border: '1px solid var(--shell-border)', borderRadius: 6, color: 'var(--shell-text)', fontSize: 11 }} />
                  <button onClick={() => {
                    const endpoint = showTalkModal.isPilot ? 'pilot-intent' : 'leads/capture';
                    const body = showTalkModal.isPilot ? {
                      corridor_id: showTalkModal.id, full_name: talkForm.name, email: talkForm.email, company: talkForm.company,
                      exposure_estimate: showTalkModal.downside?.prevented, mission_family: showTalkModal.missions?.[0],
                    } : {
                      full_name: talkForm.name, email: talkForm.email, company: talkForm.company,
                      source_type: 'corridor_talk', source_mission_id: showTalkModal.id,
                      sector_interest: 'Trade Finance / LC',
                    };
                    const token = localStorage.getItem('granullo_token') || localStorage.getItem('token');
                    fetch(`${API}/api/founder/marketing/${endpoint}`, {
                      method: 'POST', headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
                      body: JSON.stringify(body),
                    }).then(() => setTalkSent(true)).catch(() => setTalkSent(true));
                  }}
                    style={{ padding: '10px', background: showTalkModal.isPilot ? SCARLET : PURPLE, color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>
                    {showTalkModal.isPilot ? 'Request Pilot' : 'Send'}
                  </button>
                </div>
              )}
              <button onClick={() => { setShowTalkModal(null); setTalkSent(false); }} style={{ marginTop: 8, width: '100%', padding: '5px', background: 'transparent', border: '1px solid var(--shell-border)', borderRadius: 6, color: 'var(--shell-text-muted)', cursor: 'pointer', fontSize: 9 }}>Close</button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
